/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { FinancingTerm } from '@/routes/action.synchrony-finance-terms';

const MINIMUM_PURCHASE_AMOUNT_ALIASES = new Set([
    'minimumpurchaseamount',
    'minpurchaseamount',
    'minimumpurchaseamt',
    'minpurchaseamt',
]);

export function resolveMinimumPurchaseAmount(term: FinancingTerm): number | undefined {
    const raw = term as unknown as Record<string, unknown>;
    const candidateValues: unknown[] = [raw.MinimumPurchaseAmount, raw.minimumPurchaseAmount];

    // Defensive parity: some backend payloads use slight naming variants
    // (`minimumPurchaseAmt`, `minPurchaseAmount`, etc.). Accept any key that
    // semantically maps to "minimum purchase amount".
    for (const [key, value] of Object.entries(raw)) {
        const normalized = key.replace(/[_\s-]/g, '').toLowerCase();
        if (MINIMUM_PURCHASE_AMOUNT_ALIASES.has(normalized)) {
            candidateValues.push(value);
        }
    }

    // Final fallback: accept any top-level field whose key semantically contains
    // both "minimum" and "purchase" (covers unknown backend naming drift).
    for (const [key, value] of Object.entries(raw)) {
        const normalized = key.replace(/[_\s-]/g, '').toLowerCase();
        if (normalized.includes('minimum') && normalized.includes('purchase')) {
            candidateValues.push(value);
        }
    }
    for (const value of candidateValues) {
        if (typeof value === 'number' && Number.isFinite(value)) return value;
        if (typeof value === 'string' && value.trim()) {
            const parsed = parseFloat(value);
            if (Number.isFinite(parsed)) return parsed;
        }
    }
    return undefined;
}

export function isEligibleTerm(term: FinancingTerm): boolean {
    const raw = term as unknown as Record<string, unknown>;
    const candidateValues: unknown[] = [raw.IsEligible, raw.isEligible];
    for (const [key, value] of Object.entries(raw)) {
        const normalized = key.replace(/[_\s-]/g, '').toLowerCase();
        if (normalized === 'iseligible' || normalized === 'eligible') {
            candidateValues.push(value);
        }
    }
    // Final fallback for naming drift: any top-level key that contains "eligible".
    for (const [key, value] of Object.entries(raw)) {
        const normalized = key.replace(/[_\s-]/g, '').toLowerCase();
        if (normalized.includes('eligible')) {
            candidateValues.push(value);
        }
    }
    for (const value of candidateValues) {
        if (typeof value === 'boolean') return value;
        if (typeof value === 'string') {
            if (value.toLowerCase() === 'true') return true;
            if (value.toLowerCase() === 'false') return false;
        }
    }
    return true;
}

export function isTermSelectable(term: FinancingTerm, basketTotal: number): boolean {
    if (!isEligibleTerm(term)) return false;
    const minimumPurchaseAmount = resolveMinimumPurchaseAmount(term);
    if (
        minimumPurchaseAmount !== undefined &&
        Number.isFinite(minimumPurchaseAmount) &&
        basketTotal < minimumPurchaseAmount
    ) {
        return false;
    }
    return true;
}
